import { useEffect, useState } from "react";
import { differenceInDays, differenceInMonths, format, parseISO } from "date-fns";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";
import { api } from "@/lib/api";
import type { SavingsGoal, SavingsSnapshot } from "@/lib/types";

export function Finance() {
  const [goals, setGoals] = useState<SavingsGoal[]>([]);
  const [snaps, setSnaps] = useState<SavingsSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  async function load() {
    const [g, s] = await Promise.all([api.goalsList(), api.snapshotsList()]);
    setGoals(g);
    setSnaps(s);
  }
  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  async function seedBike() {
    await api.goalCreate({
      name: "Street Triple 765 RS",
      target_amount: 1643134,
      target_date: "2027-12-01",
      downpayment_pct: 30,
      monthly_contribution: 26000,
      notes: "On-road Bangalore ₹16.13L + ₹30k buffer; ₹5L DP target",
    });
    await load();
  }

  if (loading) return <p className="text-muted">Loading...</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Finance</h1>
          <p className="text-sm text-muted">Goals, countdown, affordability.</p>
        </div>
        <button
          onClick={() => setShowNew(!showNew)}
          className={showNew ? "btn-ghost" : "btn-primary"}
        >
          {showNew ? "Close" : "+ New goal"}
        </button>
      </div>

      {showNew && (
        <NewGoalForm onCreated={async () => { await load(); setShowNew(false); }} />
      )}

      {goals.length === 0 && !showNew && (
        <div className="card text-center space-y-4 py-10">
          <div>
            <h2 className="text-xl font-semibold">No goal yet</h2>
            <p className="text-sm text-muted">Start with the Street Triple 765 RS.</p>
          </div>
          <button onClick={seedBike} className="btn-primary">Seed bike goal</button>
        </div>
      )}

      {goals.map((g) => (
        <GoalCard
          key={g.id}
          goal={g}
          snapshots={snaps.filter((s) => s.goal_id === g.id)}
          onChanged={load}
        />
      ))}
    </div>
  );
}

function NewGoalForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [targetAmount, setTargetAmount] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [dpAmount, setDpAmount] = useState("");
  const [monthly, setMonthly] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const ta = Number(targetAmount);
      const dp = Number(dpAmount) || ta;
      await api.goalCreate({
        name,
        target_amount: ta,
        target_date: targetDate,
        downpayment_pct: ta > 0 ? Math.round((dp / ta) * 10000) / 100 : 100,
        monthly_contribution: monthly ? Number(monthly) : null,
        notes: null,
      });
      onCreated();
    } catch (err) { alert(String(err)); } finally { setSaving(false); }
  }

  return (
    <form onSubmit={submit} className="card space-y-3">
      <div>
        <label className="label">Goal name</label>
        <input className="field" value={name} onChange={(e) => setName(e.target.value)} required
          placeholder="e.g. Emergency fund, Laptop, House DP" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Target amount (₹)</label>
          <input type="number" inputMode="numeric" className="field" required
            value={targetAmount} onChange={(e) => setTargetAmount(e.target.value)} />
        </div>
        <div>
          <label className="label">Target date</label>
          <input type="date" className="field" required
            value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
        </div>
        <div>
          <label className="label">Downpayment (₹) — leave blank = full price</label>
          <input type="number" inputMode="numeric" className="field"
            value={dpAmount} onChange={(e) => setDpAmount(e.target.value)} />
        </div>
        <div>
          <label className="label">Planned monthly (₹)</label>
          <input type="number" inputMode="numeric" className="field"
            value={monthly} onChange={(e) => setMonthly(e.target.value)} />
        </div>
      </div>
      <button className="btn-primary w-full" disabled={saving}>
        {saving ? "Creating..." : "Create goal"}
      </button>
    </form>
  );
}

function GoalCard({
  goal, snapshots, onChanged,
}: { goal: SavingsGoal; snapshots: SavingsSnapshot[]; onChanged: () => void }) {
  const [balance, setBalance] = useState("");
  const [monthly, setMonthly] = useState(String(goal.monthly_contribution ?? ""));
  const [targetAmt, setTargetAmt] = useState(String(goal.target_amount));
  const [targetDate, setTargetDate] = useState(goal.target_date);
  const [dpAmount, setDpAmount] = useState(String(Math.round(goal.target_amount * (goal.downpayment_pct / 100))));
  const [savingBalance, setSavingBalance] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const sorted = [...snapshots].sort((a, b) => a.date.localeCompare(b.date));
  const latest = sorted.at(-1);
  const current = latest?.amount ?? 0;
  const downpayment = goal.target_amount * (goal.downpayment_pct / 100);
  const loanAmount = goal.target_amount - downpayment;
  const emi = computeEMI(loanAmount, 10, 5);

  const target = parseISO(goal.target_date);
  const today = new Date();
  const daysLeft = differenceInDays(target, today);
  const DAYS_PER_MONTH = 30.4375;
  const monthsLeft = Math.max(0.1, daysLeft / DAYS_PER_MONTH);
  const monthsLeftDisplay = Math.max(1, differenceInMonths(target, today));
  const daysSpanSorted = sorted.length > 1
    ? Math.max(1, differenceInDays(parseISO(sorted[sorted.length - 1].date), parseISO(sorted[0].date)))
    : 0;
  const paceMonthly = sorted.length >= 2
    ? ((sorted.at(-1)!.amount - sorted[0].amount) / daysSpanSorted) * DAYS_PER_MONTH
    : Number(monthly) || 0;
  const plannedMonthly = Number(monthly) || paceMonthly || 0;

  const monthsToDP = plannedMonthly > 0 ? (downpayment - current) / plannedMonthly : null;
  const monthsToFull = plannedMonthly > 0 ? (goal.target_amount - current) / plannedMonthly : null;
  const dpDate = monthsToDP !== null ? addMonths(today, Math.ceil(monthsToDP)) : null;
  const fullDate = monthsToFull !== null ? addMonths(today, Math.ceil(monthsToFull)) : null;
  const requiredForDP = Math.max(0, (downpayment - current) / monthsLeft);
  const requiredForFull = Math.max(0, (goal.target_amount - current) / monthsLeft);

  const dpPct = Math.min(100, (current / downpayment) * 100);
  const fullPct = Math.min(100, (current / goal.target_amount) * 100);
  const dpOvershoot = monthsToDP !== null ? (monthsToDP - monthsLeft) * DAYS_PER_MONTH : Infinity;
  const trackState: "ok" | "close" | "behind" =
    dpOvershoot <= 0 ? "ok" : dpOvershoot <= 30 ? "close" : "behind";
  const trackLabel = trackState === "ok" ? "on track" : trackState === "close" ? `${Math.ceil(dpOvershoot)}d late` : "behind pace";

  async function saveSnapshot(e: React.FormEvent) {
    e.preventDefault();
    const n = Number(balance);
    if (!n || isNaN(n)) return;
    setSavingBalance(true);
    try {
      await api.snapshotCreate({
        goal_id: goal.id,
        date: format(new Date(), "yyyy-MM-dd"),
        amount: n,
      });
      setBalance("");
      onChanged();
    } catch (err) { alert(String(err)); } finally { setSavingBalance(false); }
  }

  async function saveEdits() {
    setSavingEdit(true);
    try {
      const ta = Number(targetAmt);
      const dp = Number(dpAmount);
      const mo = Number(monthly);
      const patch: Partial<SavingsGoal> = {
        target_amount: isNaN(ta) ? goal.target_amount : ta,
        target_date: targetDate,
        downpayment_pct: ta > 0 && !isNaN(dp) ? Math.round((dp / ta) * 10000) / 100 : goal.downpayment_pct,
        monthly_contribution: isNaN(mo) ? null : mo,
      };
      await api.goalUpdate(goal.id, patch);
      onChanged();
      setShowEdit(false);
    } catch (err) { alert(String(err)); } finally { setSavingEdit(false); }
  }

  return (
    <div className="card space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold">{goal.name}</h2>
          <p className="text-sm text-muted">
            Target: {fmtINR(goal.target_amount)} by {format(target, "d MMM yyyy")}
          </p>
        </div>
        <button onClick={() => setShowEdit(!showEdit)} className="text-xs text-muted hover:text-white">
          {showEdit ? "close" : "edit"}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Badge value={String(daysLeft)} label="days left" />
        <Badge value={String(monthsLeftDisplay)} label="months left" />
        <Badge
          value={trackState === "ok" ? "OK" : trackState === "close" ? "~" : "!"}
          label={trackLabel}
          color={trackState === "ok" ? "accent" : trackState === "close" ? "warn" : "danger"}
        />
      </div>

      <div className="space-y-4">
        <Progress label={`Downpayment (${goal.downpayment_pct}%)`} current={current} goal={downpayment} color="#10b981" />
        <Progress label="Full price" current={current} goal={goal.target_amount} color="#f59e0b" />
      </div>

      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <Stat label="Current savings" value={fmtINR(current)} />
        <Stat label="Your pace" value={`${fmtINR(plannedMonthly)}/mo`} />
        <Stat label="Required for downpayment by target" value={fmtINR(requiredForDP) + "/mo"} />
        <Stat label="Required for full price by target" value={fmtINR(requiredForFull) + "/mo"} />
        <Stat
          label="At current pace — downpayment ready"
          value={dpDate ? format(dpDate, "MMM yyyy") : "—"}
          flag={trackState === "ok" ? "ok" : "warn"}
        />
        <Stat label="At current pace — full price ready" value={fullDate ? format(fullDate, "MMM yyyy") : "—"} />
        <Stat label="EMI estimate (₹14.4L @ 10%, 5y)" value={`₹${Math.round(emi).toLocaleString("en-IN")}/mo`} />
        <Stat label="Post-bike monthly burn" value={`EMI + fuel/ins ≈ ₹${Math.round(emi + 8000).toLocaleString("en-IN")}`} />
      </div>

      <form onSubmit={saveSnapshot} className="space-y-3 border-t border-border pt-4">
        <label className="label">Update current balance (₹)</label>
        <div className="flex gap-2">
          <input type="number" inputMode="numeric" className="field flex-1" placeholder="e.g. 150000"
            value={balance} onChange={(e) => setBalance(e.target.value)} />
          <button className="btn-primary" disabled={savingBalance}>{savingBalance ? "…" : "Log"}</button>
        </div>
      </form>

      {sorted.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Progress</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={sorted}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1f1f1f" />
              <XAxis dataKey="date" stroke="#737373" />
              <YAxis stroke="#737373" tickFormatter={(v) => `${(v / 100000).toFixed(1)}L`} />
              <Tooltip
                contentStyle={{ background: "#111", border: "1px solid #1f1f1f" }}
                formatter={(v: number) => fmtINR(v)}
              />
              <ReferenceLine y={downpayment} stroke="#10b981" strokeDasharray="4 4" label={{ value: "DP", fill: "#10b981", fontSize: 10 }} />
              <ReferenceLine y={goal.target_amount} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: "Full", fill: "#f59e0b", fontSize: 10 }} />
              <Area type="monotone" dataKey="amount" stroke="#10b981" fill="url(#g)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {showEdit && (
        <div className="border-t border-border pt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Target amount (₹)</label>
              <input type="number" inputMode="numeric" className="field"
                value={targetAmt} onChange={(e) => setTargetAmt(e.target.value)} />
            </div>
            <div>
              <label className="label">Target date</label>
              <input type="date" className="field"
                value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
            </div>
            <div>
              <label className="label">Downpayment (₹)</label>
              <input type="number" inputMode="numeric" className="field"
                value={dpAmount} onChange={(e) => setDpAmount(e.target.value)} />
              <p className="text-xs text-muted mt-1">
                = {targetAmt && Number(targetAmt) > 0
                  ? `${((Number(dpAmount) / Number(targetAmt)) * 100).toFixed(1)}%`
                  : "—"} of target
              </p>
            </div>
            <div>
              <label className="label">Monthly contribution (₹)</label>
              <input type="number" inputMode="numeric" className="field"
                value={monthly} onChange={(e) => setMonthly(e.target.value)} />
            </div>
          </div>
          <button onClick={saveEdits} disabled={savingEdit} className="btn-primary w-full">
            {savingEdit ? "Saving..." : "Save changes"}
          </button>
        </div>
      )}
    </div>
  );
}

function Badge({ value, label, color = "accent" }: { value: string; label: string; color?: "accent" | "danger" | "warn" }) {
  const cls = color === "danger" ? "text-danger" : color === "warn" ? "text-warn" : "text-accent";
  return (
    <div className="bg-bg border border-border rounded-lg p-3 text-center">
      <div className={`text-3xl font-mono font-bold ${cls}`}>{value}</div>
      <div className="text-xs text-muted uppercase">{label}</div>
    </div>
  );
}

function Progress({ label, current, goal, color }: { label: string; current: number; goal: number; color: string }) {
  const pct = Math.min(100, (current / goal) * 100);
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span>{label}</span>
        <span className="font-mono">{fmtINR(current)} / {fmtINR(goal)}</span>
      </div>
      <div className="h-2 bg-bg border border-border rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function Stat({ label, value, flag }: { label: string; value: string; flag?: "ok" | "warn" }) {
  const color = flag === "warn" ? "text-warn" : flag === "ok" ? "text-accent" : "";
  return (
    <div className="bg-bg border border-border rounded-lg p-3">
      <div className="text-xs text-muted uppercase tracking-wider">{label}</div>
      <div className={`font-mono font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function fmtINR(n: number): string {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}k`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}

function computeEMI(principal: number, ratePct: number, years: number): number {
  const r = ratePct / 100 / 12;
  const n = years * 12;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function addMonths(d: Date, m: number): Date {
  const nd = new Date(d);
  nd.setMonth(nd.getMonth() + m);
  return nd;
}
